// src/components/Eventos/EventsHeats/components/VerticalAttemptCard.jsx
import React from 'react';
import { Box, Typography, Card, Chip, useTheme, useMediaQuery } from '@mui/material';
import { getFontSize } from '../utils/eventHelpers';

const VerticalAttemptCard = ({ 
  height,
  attempts = [], // Array de intentos para esta altura ['X', 'O', 'P', '', '', '']
  position, 
  positionChange,
  isCurrentHeight = false, // Si es la altura actual en la que está compitiendo
  isBestHeight = false, // Si es la mejor altura conseguida por el atleta
  isActive = false // Si está activo/seleccionado
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));

  const getPositionChangeDisplay = () => {
    if (!positionChange || positionChange.val === 0) {
      return {
        text: '0',
        color: theme.palette.text.secondary
      };
    }
    
    if (positionChange.dir === 'up') {
      return {
        text: `+${positionChange.val}`,
        color: theme.palette.success.main
      };
    } else {
      return {
        text: `-${positionChange.val}`,
        color: theme.palette.error.main
      };
    }
  };

  const getBorderColor = () => {
    if (isBestHeight) return theme.palette.success.main;
    if (isCurrentHeight) return theme.palette.warning.main;
    return '#e0e0e0';
  };

  const getHeaderBackgroundColor = () => {
    if (isBestHeight) return theme.palette.success.main;
    if (isCurrentHeight) return theme.palette.warning.main;
    return '#f5f5f5';
  };

  const SIZE = isXs ? 24 : 28;          // lado del cuadrado

const baseChipStyle = {
  width:  SIZE,
  height: SIZE,
  borderRadius: 4,                    // ← cuadrado con esquinas suaves
  fontWeight: 'bold',
  fontSize: getFontSize(14, 1, isXs, isSm, isMd),
  '& .MuiChip-label': {
    padding: 0,
    lineHeight: 1,
  },
};

const getAttemptChipProps = (attempt) => {
    switch (attempt) {
      case 'O': // válido – verde
        return {
          variant: 'filled',
          sx: {
            ...baseChipStyle,
            backgroundColor: '#4caf50',
            color: '#fff',
          },
        };
      case 'X': // fallo – rojo
        return {
          variant: 'filled',
          sx: {
            ...baseChipStyle,
            backgroundColor: '#f44336',
            color: '#fff',
          },
        };
      case 'P': // pase – azul
        return {
          variant: 'filled',
          sx: {
            ...baseChipStyle,
            backgroundColor: '#2196f3',
            color: '#fff',
          },
        };
      case '-': // pase (alt) – azul claro
        return {
          variant: 'filled',
          sx: {
            ...baseChipStyle,
            backgroundColor: '#03a9f4',
            color: '#fff',
          },
        };
      case 'R': // retirada – naranja
        return {
          variant: 'filled',
          sx: {
            ...baseChipStyle,
            backgroundColor: '#ff9800',
            color: '#fff',
          },
        };
      default:  // intento vacío – gris
        return {
          variant: 'outlined',
          sx: {
            ...baseChipStyle,
            borderColor: '#bdbdbd',
            color: '#bdbdbd',
            fontWeight: 'normal',
            fontSize: getFontSize(12, 1, isXs, isSm, isMd),
          },
        };
    }
  };

  const positionChangeDisplay = getPositionChangeDisplay();

  // Filtrar solo los intentos que tienen datos (no están vacíos)
  const validAttempts = attempts.filter(attempt => 
    attempt && 
    typeof attempt === 'string' && 
    attempt.trim() !== ''
  );

  // Determinar el texto de la etiqueta según el número de intentos
  const getAttemptsLabel = () => {
    if (validAttempts.length === 0) return 'Sin intentos';
    if (validAttempts.length === 1) return 'Intento';
    if (validAttempts.length === 2) return 'Intentos (1-2)';
    return 'Intentos (1-3)';
  };

  return (
    <Card
      sx={{
        position: 'relative',
        minHeight: isXs ? 100 : 120,
        backgroundColor: isActive ? '#e3f2fd' : '#ffffff',
        border: `2px solid ${getBorderColor()}`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        }
      }}
    >
      {/* Sección superior - Altura */}
      <Box
        sx={{
          backgroundColor: getHeaderBackgroundColor(),
          color: (isBestHeight || isCurrentHeight) ? 'white' : theme.palette.text.primary,
          py: 1,
          px: 2,
          textAlign: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: getFontSize(16, 1, isXs, isSm, isMd),
            fontWeight: 'bold',
            lineHeight: 1
          }}
        >
          {height ? `${height.toFixed(2)}m` : '-'}
        </Typography>
        {isBestHeight && (
          <Typography
            sx={{
              fontSize: getFontSize(10, 1, isXs, isSm, isMd),
              lineHeight: 1,
              opacity: 0.9
            }}
          >
            Mejor
          </Typography>
        )}
        {isCurrentHeight && !isBestHeight && (
          <Typography
            sx={{
              fontSize: getFontSize(10, 1, isXs, isSm, isMd),
              lineHeight: 1,
              opacity: 0.9
            }}
          >
            Actual
          </Typography>
        )}
      </Box>

      {/* Posición en esquina superior izquierda */}
      <Box
        sx={{
          position: 'absolute',
          top: isXs ? 50 : 55,
          left: 8,
          zIndex: 2
        }}
      >
        <Typography
          sx={{
            fontSize: getFontSize(14, 1, isXs, isSm, isMd),
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            backgroundColor: 'transparent',
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
            minWidth: 20,
            textAlign: 'center',
            border: `1px solid ${theme.palette.primary.main}`
          }}
        >
          {position || '-'}
        </Typography>
      </Box>

      {/* Cambio de posición en esquina superior derecha */}
      <Box
        sx={{
          position: 'absolute',
          top: isXs ? 50 : 55,
          right: 8,
          zIndex: 2
        }}
      >
        <Typography
          sx={{
            fontSize: getFontSize(12, 1, isXs, isSm, isMd),
            fontWeight: 'bold',
            color: positionChangeDisplay.color,
            backgroundColor: 'transparent',
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
            minWidth: 20,
            textAlign: 'center',
            border: `1px solid ${positionChangeDisplay.color}`
          }}
        >
          {positionChangeDisplay.text}
        </Typography>
      </Box>

      {/* Sección inferior - Intentos */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: isXs ? 68 : 78,
          px: 1,
          flexDirection: 'column',
          gap: 1
        }}
      >
        {/* Etiqueta de intentos con conteo dinámico */}
        <Typography
          sx={{
            fontSize: getFontSize(11, 1, isXs, isSm, isMd),
            color: theme.palette.text.secondary,
            lineHeight: 1,
            textAlign: 'center',
            mb: 0.5
          }}
        >
          {getAttemptsLabel()}
        </Typography>

        {/* Chips de intentos dinámicos */}
        {validAttempts.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'nowrap',
              width: '100%'
            }}
          >
            {validAttempts.map((attempt, index) => (
              <Chip
                key={index}
                label={attempt}
                size="small"
                {...getAttemptChipProps(attempt)}
                sx={{
                  ...getAttemptChipProps(attempt).sx,
                  flex: '0 0 auto',      // ← evita stretching
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: getFontSize(12, 1, isXs, isSm, isMd),
              color: theme.palette.text.disabled,
              fontStyle: 'italic',
              textAlign: 'center'
            }}
          >
            No hay datos
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default VerticalAttemptCard;